"use client";

import { BadgeCheck, ChevronRight, Headphones, ShieldCheck } from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";
import { Sheet } from "@/components/ui/sheet";
import type { TransactionProductDto } from "@/content/types";
import { formatMoney } from "@/lib/money";
import { cn } from "@/lib/cn";
import { BuyActionBar } from "./buy-action-bar";
import { SkuSheet } from "./sku-sheet";

function DetailRow({
  title,
  value,
  icon,
  onClick,
  hirudin = false,
}: {
  title: string;
  value: string;
  icon: ReactNode;
  onClick: () => void;
  hirudin?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex min-h-[52px] w-full items-center gap-3 border-b border-line px-4 py-3 text-left last:border-b-0 focus-visible:outline-2 focus-visible:-outline-offset-2",
        hirudin ? "focus-visible:outline-hirudin" : "focus-visible:outline-forest-500",
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "flex size-5 shrink-0 items-center justify-center [&>svg]:size-5 [&>svg]:stroke-[1.5]",
          hirudin ? "text-hirudin" : "text-forest-500",
        )}
      >
        {icon}
      </span>
      <span className="min-w-0 flex-1 text-[15px] leading-[22px] text-ink">{title}</span>
      <span className="max-w-[42%] truncate text-xs leading-[18px] text-ink-3">
        {value}
      </span>
      <ChevronRight className="size-3 shrink-0 stroke-[1.5] text-ink-3" aria-hidden="true" />
    </button>
  );
}

export function ProductDetailActions({
  product,
  initialInviteCode,
}: {
  product: TransactionProductDto;
  initialInviteCode: string;
}) {
  const [skuOpen, setSkuOpen] = useState(false);
  const [serviceOpen, setServiceOpen] = useState(false);
  const [consultOpen, setConsultOpen] = useState(false);
  const defaultSku =
    product.skus.find((sku) => sku.id === product.defaultSkuId) ?? product.skus[0];
  const hirudin = product.line === "hirudin";

  return (
    <>
      <div
        className={cn(
          "overflow-hidden rounded-[10px] border bg-card",
          hirudin ? "border-hirudin/25" : "border-line",
        )}
      >
        <DetailRow
          title="规格"
          value={defaultSku.spec}
          icon={<BadgeCheck />}
          onClick={() => setSkuOpen(true)}
          hirudin={hirudin}
        />
        <DetailRow
          title={product.line === "herbal" ? "产品与服务说明" : "正品溯源 · 批次可查"}
          value=""
          icon={<ShieldCheck />}
          onClick={() => setServiceOpen(true)}
          hirudin={hirudin}
        />
      </div>

      <BuyActionBar
        label={
          product.displayPrice
            ? `立即购买 ${formatMoney(product.displayPrice)}`
            : (product.availabilityCopy ?? "该商品暂未开放购买")
        }
        onClick={() => setSkuOpen(true)}
        leftSlot={
          <button
            type="button"
            onClick={() => setConsultOpen(true)}
            className="flex size-11 shrink-0 flex-col items-center justify-center gap-px text-[10px] leading-3 text-ink-2 focus-visible:outline-2 focus-visible:outline-forest-500"
          >
            <Headphones className="size-5 stroke-[1.5]" aria-hidden="true" />
            客服
          </button>
        }
        className="supports-[backdrop-filter]:bg-card/95"
      />

      <SkuSheet
        product={product}
        open={skuOpen}
        onOpenChange={setSkuOpen}
        initialInviteCode={initialInviteCode}
      />

      <Sheet open={serviceOpen} onOpenChange={setServiceOpen} title="服务说明">
        <div className="space-y-3 text-sm leading-[1.7] text-ink-2">
          <p>{product.line === "herbal" ? "单盒装，每盒 2.0 克，价格尚未确定。" : "正品溯源 · 批次可查"}</p>
          <p>当前演示版保留批次号、产地与检测信息展示位,正式版将接入完整溯源资料。</p>
        </div>
      </Sheet>

      <Sheet open={consultOpen} onOpenChange={setConsultOpen} title="咨询客服">
        <div className="space-y-3 text-sm leading-[1.7] text-ink-2">
          <p>客服渠道接入中，请先通过页面底部联系方式咨询。</p>
          <p>售前咨询、批次查询与售后支持将在正式上线后统一开放。</p>
        </div>
      </Sheet>
    </>
  );
}
